import type { CalendarEntry } from "../catalog.js";
import { SITE_LOCALE } from "../ui/dom.js";

/**
 * The heading over the fixture list.
 *
 * It used to be a constant "Today · matches". On an evening when every round is
 * the next day, the first row under "Today" is a day header for another date —
 * which reads as "the calendar isn't showing", not as "come back tomorrow". So
 * the heading states what the calendar actually holds, resolved in the
 * VIEWER's timezone (a 10:00 UTC round is not "today" for a reader in Los
 * Angeles the evening before).
 */
export interface FixturesHeading {
  title: string;
  eyebrow: string;
}

/** The unchanged heading: the honest answer when there is nothing dated to say. */
const PLAIN: FixturesHeading = { title: "Today", eyebrow: "matches" };

/**
 * `2026-09-05` for the given instant, as seen from `timeZone`.
 *
 * Exported because the hub groups the calendar by the same day boundary this
 * heading is decided by; two definitions of "the viewer's day" would let the
 * heading say "Today" over a shelf that had filed the round under yesterday.
 */
export function localDay(ms: number, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(ms));
}

/** `Sat 5 Sep, 11:00`: the site's date style (`SITE_LOCALE`), in the viewer's timezone. */
export function localWhen(ms: number, timeZone: string | undefined, locale: string | undefined = SITE_LOCALE): string {
  const date = new Date(ms);
  const parts = new Intl.DateTimeFormat(locale, { timeZone, weekday: "short", day: "numeric", month: "short" }).formatToParts(date);
  // en-GB abbreviates September as "Sept"; the site's other dates ("Jul 2017") use three letters.
  const day = parts.map((p) => (p.type === "month" ? p.value.slice(0, 3) : p.value)).join("");
  const time = new Intl.DateTimeFormat(locale, { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(date);
  return `${day}, ${time}`;
}

/**
 * `active` anywhere wins outright — a live round is the most important thing on
 * the page and does not need a date. Otherwise the question is whether anything
 * still to come falls on the reader's own calendar day; if not, name the next
 * one so the empty "today" is explained rather than mysterious.
 *
 * `timeZone`/`locale` are injectable only so the tests can pin a viewer; both
 * default to the browser's own, exactly like `formatDay`/`formatTime` in
 * `ui/dom.ts`.
 */
export function fixturesHeading(
  calendar: readonly CalendarEntry[],
  now: number,
  timeZone?: string,
  locale?: string,
): FixturesHeading {
  if (calendar.some((c) => c.state === "active")) return { title: "Live now", eyebrow: "matches" };

  const today = localDay(now, timeZone);
  const scheduled = calendar
    .filter((c) => c.state !== "finished" && c.startsAtMs !== null)
    .map((c) => c.startsAtMs as number);
  if (scheduled.some((ms) => localDay(ms, timeZone) === today)) return PLAIN;

  // Not "the first in the list": the calendar is grouped by state, so the
  // earliest upcoming round is the one to name.
  const next = scheduled.filter((ms) => ms >= now).sort((a, b) => a - b)[0];
  if (next === undefined) return PLAIN;
  return { title: "Next round", eyebrow: localWhen(next, timeZone, locale) };
}
